/**
 * useEditorBridge — the request/response channel between the agent-panel
 * iframe and the fork's branding.js in the enclosing Collabora frame.
 *
 * Every request is a `{MessageId, SendTime, Values}` envelope posted to
 * window.parent with a fresh `requestId`; the reply (whatever its MessageId)
 * echoes that id and resolves the pending promise. A request that gets no
 * answer within its budget resolves `null`, which callers report as a failed
 * apply — never as success. Outside an editor (no parent frame) every call
 * resolves `null` immediately.
 *
 * Two families ride the same channel:
 *   - in-place agent edits: Zehn_ApplyOps / Zehn_RequestSave / Zehn_GetSelection
 *   - template placeholders (Writer bookmarks named zph_<key>__<nn>):
 *     Zehn_ListBookmarks / Zehn_BookmarkAtCaret / Zehn_InsertBookmark /
 *     Zehn_FillBookmarks / Zehn_DeleteBookmarks / Zehn_JumpToBookmark, all
 *     answered by Zehn_BookmarkResult {ok, detail:[{reason}], ...extras}.
 *     Values are sent as PLAIN TEXT — branding.js is the only thing that
 *     turns them into the HTML the core pastes.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { configuredCollaboraOrigin } from '@/shared/utils/collaboraOrigin.js';

export const BOOKMARK_PREFIX = 'zph_';

// Requests and replies are pinned to the configured Collabora origin
// (VITE_COLLABORA_URL): they carry document text (selections, edit ops,
// placeholder values). The old target was document.referrer with a '*'
// fallback — and cool.html is served with `Referrer-Policy: no-referrer`, so
// in production it was always '*'. Unconfigured = nothing is sent (a request
// then resolves null, which callers report as a failed apply).
let warnedUnconfigured = false;

export function postToEditor(messageId, values) {
  if (window.parent === window) return;
  const origin = configuredCollaboraOrigin();
  if (!origin) {
    if (!warnedUnconfigured) {
      warnedUnconfigured = true;
      console.warn('[editor-bridge] VITE_COLLABORA_URL is not configured; the panel cannot reach the editor');
    }
    return;
  }
  window.parent.postMessage(
    JSON.stringify({ MessageId: messageId, SendTime: Date.now(), Values: values }),
    origin,
  );
}

/** Fire-and-forget event for the WOPI host (branding.js relays it verbatim). */
export function postPanelEvent(values) {
  postToEditor('Zehn_PanelEvent', values);
}

export function useEditorBridge() {
  const pendingRef = useRef(new Map());
  const seqRef = useRef(0);

  useEffect(() => {
    function onMessage(event) {
      // Our framer (not a sibling or opener) AND the Collabora origin.
      if (event.source !== window.parent) return;
      if (!event.origin || event.origin !== configuredCollaboraOrigin()) return;
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      const values = msg?.Values;
      const pending = values && pendingRef.current.get(values.requestId);
      if (!pending) return;
      clearTimeout(pending.timer);
      pendingRef.current.delete(values.requestId);
      pending.resolve(values);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const editorRequest = useCallback((messageId, values, timeoutMs) => {
    if (window.parent === window) return Promise.resolve(null);
    return new Promise((resolve) => {
      const requestId = `zehn-${Date.now()}-${(seqRef.current += 1)}`;
      const timer = setTimeout(() => {
        pendingRef.current.delete(requestId);
        resolve(null); // timed out — the caller reports a failed apply
      }, timeoutMs);
      pendingRef.current.set(requestId, { resolve, timer });
      postToEditor(messageId, { ...values, requestId });
    });
  }, []);

  return useMemo(
    () => ({
      // Any request by name: the dock page relays the aip panel's editor work
      // (a fixed list of MessageIds, checked by the relay) through this.
      request: editorRequest,
      // Each op costs several sequential search round-trips in the editor
      // (probes, chunk programs, verify passes) — scale the budget with the
      // batch instead of letting a legitimate multi-op apply hit a fixed
      // ceiling and report a spurious failure.
      applyOps: (ops) => editorRequest('Zehn_ApplyOps', { ops }, Math.min(10000 + 8000 * ops.length, 60000)),
      requestSave: () => editorRequest('Zehn_RequestSave', {}, 8000),
      // Current editor text selection (plain text; null on timeout/none) —
      // lets deictic chat requests ("translate this") target the real spot.
      getSelection: () => editorRequest('Zehn_GetSelection', {}, 2000),

      // ---- template placeholders ------------------------------------
      // → {ok, names: [...document order]}
      listBookmarks: (prefix = BOOKMARK_PREFIX) =>
        editorRequest('Zehn_ListBookmarks', { prefix }, 4000),
      // → {ok, name: string|null} — the placeholder bookmark under the caret
      bookmarkAtCaret: (prefix = BOOKMARK_PREFIX) =>
        editorRequest('Zehn_BookmarkAtCaret', { prefix }, 3000),
      // → {ok, names} — inserts `text` at the caret wrapped in bookmark `name`
      insertBookmark: ({ name, text }) =>
        editorRequest('Zehn_InsertBookmark', { name, text }, 6000),
      // fills: [{key, text}] → {ok, filled:[{key,count}], missing:[key], names}
      fillBookmarks: (fills) =>
        editorRequest('Zehn_FillBookmarks', { fills }, Math.min(4000 + 1500 * fills.length, 30000)),
      // → {ok, names, removed} — blanks every placement of `key`, then unlinks
      deleteBookmarks: (key) => editorRequest('Zehn_DeleteBookmarks', { key }, 6000),
      // → {ok, names} — rewrites ONE placement's text (a mark that grew over
      // pasted boilerplate gets its marker back); `name` must match one mark
      updateBookmark: ({ name, text }) => editorRequest('Zehn_UpdateBookmark', { name, text }, 6000),
      // → {ok, names} — drops ONE mark and keeps its text in the document
      unlinkBookmark: (name) => editorRequest('Zehn_UnlinkBookmark', { name }, 6000),
      // → {ok} — moves the caret (and viewport) to the bookmark
      jumpToBookmark: (name) => editorRequest('Zehn_JumpToBookmark', { name }, 2000),
    }),
    [editorRequest],
  );
}
